/**
 * UnifiedSearchBar
 *
 * Vocabulary-powered search + compose bar for the play editor.
 * Coaches type a play call string (e.g., "3 I Z 44 Power") and chips
 * are progressively built from vocabulary. Also supports searching
 * existing formations and concepts via the legacy search API.
 */

import { useEffect, useRef, useState } from 'react'
import { Search, Plus } from 'lucide-react'
import type { VocabularyEntry } from '@/db/Types'
import type { PlayCallChip } from '@/types/BlockProperties'
import { PlayCallChip as PlayCallChipComponent } from '@/components/chips/PlayCallChip'
import { SearchDropdown } from './SearchDropdown'
import type { PlayMatch } from './SearchDropdown'
import { useUnifiedSearch } from '@/hooks/UseUnifiedSearch'
import { useVocabularyData } from '@/hooks/UseVocabularyData'
import { useComposeMode } from '@/hooks/UseComposeMode'
import './unified-search-bar.css'

interface UnifiedSearchBarProps {
	teamId: string | null
	playbookId?: string
	placeholder?: string
	initialChips?: PlayCallChip[]
	onChipsChange?: (chips: PlayCallChip[]) => void
	onVocabularySelect: (entry: VocabularyEntry) => void
	onPlaySelect: (playId: string) => void
	onCreatePlay?: (chips: PlayCallChip[], name: string) => void
}

export function UnifiedSearchBar({
	teamId,
	playbookId,
	placeholder = 'Type play call or search...',
	initialChips,
	onChipsChange,
	onVocabularySelect,
	onPlaySelect,
	onCreatePlay,
}: UnifiedSearchBarProps) {
	const inputRef = useRef<HTMLInputElement>(null)
	const containerRef = useRef<HTMLDivElement>(null)
	const [showDropdown, setShowDropdown] = useState(false)

	// Vocabulary data (only when playbookId exists)
	const { vocabulary } = useVocabularyData(playbookId ?? '')

	// Compose mode: progressive chip building from vocabulary
	const {
		chips,
		pendingText,
		suggestions,
		highlightedIndex,
		isComposing,
		handleInputChange,
		handleKeyDown,
		handleSuggestionSelect,
		removeChip,
		replaceChip,
		clearAll,
		playCallName,
	} = useComposeMode({ vocabulary: playbookId ? vocabulary : [], initialChips, onChipsChange })

	// Legacy search for formations/concepts
	const { setQuery, results, isSearching } = useUnifiedSearch(teamId ?? undefined, playbookId)

	// Map legacy search results to PlayMatch format for the dropdown
	const playMatches: PlayMatch[] = results
		? [
			...results.formations.map(r => ({ id: r.id, name: r.name })),
			...results.concepts.map(r => ({ id: r.id, name: r.name })),
		]
		: []

	// Close dropdown on click outside
	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setShowDropdown(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	function handleInput(value: string) {
		handleInputChange(value)
		setQuery(value)
		if (value.trim()) {
			setShowDropdown(true)
		}
	}

	function handleInputFocus() {
		if (pendingText.trim()) {
			setShowDropdown(true)
		}
	}

	function handleSuggestionSelectAndApply(entry: VocabularyEntry) {
		handleSuggestionSelect(entry)
		onVocabularySelect(entry)
		setShowDropdown(false)
		inputRef.current?.focus()
	}

	function handlePlaySelect(playId: string) {
		onPlaySelect(playId)
		setShowDropdown(false)
	}

	function handleCreatePlay() {
		if (onCreatePlay && chips.length > 0) {
			onCreatePlay(chips, playCallName)
			clearAll()
		}
	}

	return (
		<div ref={containerRef} className="unified-search-bar" data-composing={isComposing}>
			<div className="unified-search-bar__input-wrapper">
				<Search className="unified-search-bar__icon" />
				<div className="unified-search-bar__chips">
					{chips.map((chip, i) => (
						<PlayCallChipComponent
							key={`${chip.text}-${i}`}
							text={chip.text}
							category={chip.category}
							isResolved={chip.vocabulary_id !== null}
							onRemove={() => removeChip(i)}
							onTextChange={chip.vocabulary_id === null ? (newText) => replaceChip(i, { ...chip, text: newText }) : undefined}
						/>
					))}
					<input
						ref={inputRef}
						type="text"
						value={pendingText}
						onChange={e => handleInput(e.target.value)}
						onKeyDown={handleKeyDown}
						onFocus={handleInputFocus}
						placeholder={chips.length === 0 ? placeholder : ''}
						className="unified-search-bar__input"
					/>
				</div>
			</div>

			{isComposing && (
				<div className="unified-search-bar__compose-bar">
					<span className="unified-search-bar__compose-label">Play call:</span>
					<span className="unified-search-bar__compose-name">{playCallName}</span>
					{onCreatePlay && (
						<button
							type="button"
							className="unified-search-bar__create-button"
							onClick={handleCreatePlay}
						>
							<Plus size={14} /> Create Play
						</button>
					)}
				</div>
			)}

			{showDropdown && (
				<SearchDropdown
					suggestions={suggestions}
					playResults={playMatches}
					isSearching={isSearching}
					query={pendingText}
					highlightedIndex={highlightedIndex}
					onSuggestionSelect={handleSuggestionSelectAndApply}
					onPlaySelect={handlePlaySelect}
					onClose={() => setShowDropdown(false)}
				/>
			)}
		</div>
	)
}
